import { PropsWithChildren } from 'react'
import {
  Transition,
  Dialog,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react'
import { VariantProps, tv } from 'tailwind-variants'

const modalV = tv({
  slots: {
    container: 'fixed inset-0 z-[9999] flex w-screen items-center justify-center p-4',
    backdrop:
      'fixed inset-0 z-[9998] bg-black/10 backdrop-blur-sm transition-all duration-75',
    panel: 'max-w-[40rem] rounded-[1.25rem] bg-gray-850',
  },
  variants: {
    mode: {
      normal: {
        panel: 'bg-item-menu-sidebar',
      },
      blur: {
        panel: 'modal-auth-token-blur-bg',
      },
    },
    fullWidth: {
      true: {
        panel: 'w-full',
      },
    },
  },
  defaultVariants: {
    mode: 'normal',
    fullWidth: true,
  },
})

export type TModalActionRef = {
  open(): void
  close(): void
}

type TModalStaticProps = VariantProps<typeof modalV> &
  PropsWithChildren<{
    show?: boolean
    canDismiss?: boolean
    toggleShow?: (show: boolean) => void
    panelClassName?: string
    containerClassName?: string
  }>

export const ModalStatic = ({
  children,
  canDismiss = true,
  mode,
  show = false,
  toggleShow,
  fullWidth,
  panelClassName,
  containerClassName,
}: TModalStaticProps) => {
  const { backdrop, panel, container } = modalV({ mode, fullWidth })
  const containerClasses = containerClassName
    ? `${container()} ${containerClassName}`.trim()
    : container()
  const panelClasses = panelClassName
    ? `${panel()} ${panelClassName}`.trim()
    : panel()

  return (
    <Transition appear show={show}>
      <Dialog
        open={show}
        onClose={canDismiss && toggleShow ? () => toggleShow(false) : () => {}}
        className="relative z-[9999]">
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <div className={backdrop()} aria-hidden="true" />
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95">
          {/* Full-screen container to center the panel */}
          <div className={containerClasses}>
            {/* The actual dialog panel  */}
            <DialogPanel className={panelClasses}>{children}</DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}
